import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import TruckList from './TruckList';
import KpiBar from './KpiBar';
import DispatchMap from './DispatchMap';
import OverridePlanModal from './OverridePlanModal';
import AssignmentPanel from './AssignmentPanel';
import LogsPanel from './LogsPanel';
import {
  type Truck,
  type KpiData,
  type SiteZone,
  type AssetType,
  type TrafficEvent,
} from './types';
import { useAppSelector, useAppDispatch } from 'store/hook';
import { selectAssociatedAssets, selectAssetsLoaded } from 'store/asset.slice';
import { selectLatestTagData, setDevicesWithTagList } from 'store/device.slice';
import DeviceAPI from 'api/device.api';
import DispatchMonitorAPI, {
  type DispatchDevice,
  type DispatchDeviceLatest,
  type LastStatusEntry,
} from 'api/dispatch-monitor.api';
import LocationImporterAPI from 'api/location-importer.api';
import useAppTheme from 'hooks/useAppTheme';
import useTimeDisplayMode from 'hooks/useTimeDisplayMode';
import useCustomerInfo from 'hooks/useCustomerInfo';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import EventSourcePolyfillModule from 'event-source-polyfill';
import { CLIENT_THEME, clientThemePalette } from 'utils/clientTheme';

type EventSourcePolyfillType = new (
  url: string,
  init?: { headers?: Record<string, string> }
) => EventSource;

const EventSourcePolyfill = (EventSourcePolyfillModule as any)
  .EventSourcePolyfill as EventSourcePolyfillType;

dayjs.extend(utc);
dayjs.extend(timezone);

function useCurrentTime(tz: string): string {
  const [time, setTime] = useState(() => dayjs().tz(tz).format('HH:mm:ss'));
  useEffect(() => {
    const id = setInterval(() => {
      setTime(dayjs().tz(tz).format('HH:mm:ss'));
    }, 1000);
    return () => {
      clearInterval(id);
    };
  }, [tz]);
  return time;
}

const DispatchMonitorView: React.FC = () => {
  const [selectedTruckId, setSelectedTruckId] = useState<string | null>(null);
  const [overrideTruckId, setOverrideTruckId] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'trucks' | 'assignments' | 'logs'>(
    'trucks'
  );
  const [liveLogs, setLiveLogs] = useState(false);
  const liveLogsRef = useRef(false);
  liveLogsRef.current = liveLogs;
  const [sensorDataReady, setSensorDataReady] = useState(false);
  const [dispatchDataReady, setDispatchDataReady] = useState(false);
  const [dispatchDevices, setDispatchDevices] = useState<
    Record<string, DispatchDevice>
  >({});
  const [statusOverrides, setStatusOverrides] = useState<
    Record<string, string>
  >({});
  const [lastStatusMap, setLastStatusMap] = useState<
    Record<string, { status: string; reason: string }>
  >({});
  const [shiftEndedDevices, setShiftEndedDevices] = useState<
    Record<string, boolean>
  >({});
  const dispatch = useAppDispatch();
  const api = useRef(new DeviceAPI()).current;
  const dispatchApi = useRef(new DispatchMonitorAPI()).current;
  const locationImporterApi = useRef(new LocationImporterAPI()).current;
  const [importedSites, setImportedSites] = useState<SiteZone[]>([]);
  const [trafficEvents, setTrafficEvents] = useState<TrafficEvent[]>([]);
  const accessToken = useAppSelector((s) => s.authReducer.accessToken);
  const customerId = useAppSelector((s) => s.persistedReducer.customer.id);
  const { customerCode } = useCustomerInfo();
  const { t } = useTranslation();
  const { enableDarkTheme } = useAppTheme();
  const palette = enableDarkTheme
    ? clientThemePalette[CLIENT_THEME].dark
    : clientThemePalette[CLIENT_THEME].light;
  const { timezoneBasedOnDisplayMode } = useTimeDisplayMode();
  const currentTime = useCurrentTime(timezoneBasedOnDisplayMode ?? 'UTC');

  const bg = enableDarkTheme ? palette.pageBackground : palette.pageBackground;
  const topBarBg = enableDarkTheme
    ? palette.pageBackground
    : palette.toolBarBackground;
  const borderColor = palette.cardBorderColor;
  const textPrimary = enableDarkTheme ? '#e2e8f0' : '#1a202c';
  const textMuted = enableDarkTheme ? '#6b7280' : '#8A92A0';

  // Poll latest-sensor-data every 10 s to get live GPS positions
  const timeoutIdRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    let isCancelled = false;
    const fetchData = async () => {
      try {
        if (isCancelled) return;
        const data = await api.deviceLatestSensorDataAll(true);
        if (!isCancelled) {
          if (data) dispatch(setDevicesWithTagList(data));
          setSensorDataReady(true);
          timeoutIdRef.current = setTimeout(() => {
            void fetchData();
          }, 10_000);
        }
      } catch (_) {
        if (!isCancelled) {
          setSensorDataReady(true);
          timeoutIdRef.current = setTimeout(() => {
            void fetchData();
          }, 10_000);
        }
      }
    };
    void fetchData();
    return () => {
      isCancelled = true;
      if (timeoutIdRef.current) clearTimeout(timeoutIdRef.current);
    };
  }, []);

  // Initial fetch to populate state, then SSE takes over for live updates
  useEffect(() => {
    let isCancelled = false;
    const fetchDispatch = async () => {
      try {
        const res = await dispatchApi.getLatestDispatchAssetStatus(false);
        if (!isCancelled && res?.devices) {
          const map: Record<string, DispatchDevice> = {};
          res.devices.forEach((d) => {
            map[d.deviceId] = d;
          });
          setDispatchDevices(map);
        }
      } catch (_) {}
      if (!isCancelled) setDispatchDataReady(true);
    };
    void fetchDispatch();
    return () => {
      isCancelled = true;
    };
  }, []);

  // Poll last-status every 5s, accumulate log entries; only when liveLogs on
  const logsTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const mergeLastStatus = (
    prev: Record<string, DispatchDevice>,
    devices: Array<{ deviceId: string; last: LastStatusEntry | null }>
  ) => {
    const next = { ...prev };
    devices.forEach((d) => {
      if (!d.last) return;
      const existing = prev[d.deviceId]?.data ?? [];
      const lastId = d.last.id;
      const alreadyPresent = existing.some((e) => e?.id === lastId);
      next[d.deviceId] = {
        ...prev[d.deviceId],
        deviceId: d.deviceId,
        latest: prev[d.deviceId]?.latest ?? null,
        data: alreadyPresent ? existing : [d.last, ...existing],
      };
    });
    return next;
  };

  useEffect(() => {
    if (!liveLogs) return;
    let isCancelled = false;
    const fetchLogs = async () => {
      try {
        const res = await dispatchApi.getLastStatus();
        if (!isCancelled && res?.devices) {
          setDispatchDevices((prev) => mergeLastStatus(prev, res.devices));
        }
      } catch (err) {
        console.error('[getLastStatus]', err);
      }
      if (!isCancelled) {
        logsTimeoutRef.current = setTimeout(() => {
          void fetchLogs();
        }, 5_000);
      }
    };
    void fetchLogs();
    return () => {
      isCancelled = true;
      if (logsTimeoutRef.current) clearTimeout(logsTimeoutRef.current);
    };
  }, [liveLogs]);

  // SSE for live dispatch updates
  useEffect(() => {
    if (!accessToken || !customerId) return;
    const base = process.env.REACT_APP_MANAGEMENT_API_URL ?? '';
    const es = new EventSourcePolyfill(
      `${base}/api/dispatch-monitor/live-dispatch-sse?customerId=${customerId}`,
      {
        headers: { Authorization: `Bearer ${accessToken}` },
      }
    );
    es.onmessage = (e: MessageEvent) => {
      try {
        const outer = JSON.parse(e.data as string) as {
          channel: string;
          message: string | Record<string, any> | any[];
        };

        if (outer.channel === `xtab:${customerCode}:last:pubsub`) {
          const msg =
            typeof outer.message === 'string'
              ? JSON.parse(outer.message)
              : outer.message;
          const device = msg?.device as string | undefined;
          if (!device) return;

          if (msg?.action === 'SHIFT_END') {
            setShiftEndedDevices((prev) => ({ ...prev, [device]: true }));
            return;
          }

          const payloadStatus = msg?.payload?.status as string | undefined;
          const reason = msg?.payload?.reason as string | undefined;
          if (payloadStatus) {
            setLastStatusMap((prev) => ({
              ...prev,
              [device]: { status: payloadStatus, reason: reason ?? '' },
            }));
          }
          return;
        }

        if (outer.channel === `xtab:${customerCode}:trafficevents:pubsub`) {
          const parsed =
            typeof outer.message === 'string'
              ? JSON.parse(outer.message)
              : outer.message;
          const events = (
            Array.isArray(parsed) ? parsed : parsed.trafficEvents ?? []
          ) as TrafficEvent[];
          setTrafficEvents(events);
          return;
        }

        const unwrap = (val: any): any => {
          const parsed =
            typeof val === 'string'
              ? JSON.parse(val.trim().replace(/,\s*$/, ''))
              : val;
          if (parsed && typeof parsed === 'object' && 'message' in parsed) {
            return unwrap(parsed.message);
          }
          return parsed;
        };
        const msg = unwrap(outer.message) as DispatchDeviceLatest & {
          device: string;
          totalMaterialDumped?: number;
          currentDumped?: number;
        };

        if (msg?.action === 'STATUS') {
          const deviceId = msg?.device;
          const truckStatus = (msg as any)?.payload?.truckStatus as
            | string
            | undefined;
          if (deviceId && truckStatus) {
            setStatusOverrides((prev) => ({
              ...prev,
              [deviceId]: truckStatus,
            }));
          }
          return;
        }

        if (msg?.action !== 'PLAN_KPI_RECALCULATED') return;
        const deviceId = msg?.device;
        if (!deviceId) return;
        setDispatchDevices((prev) => {
          const prevLatest = prev[deviceId]?.latest;
          const latest: DispatchDeviceLatest = {
            id: msg.id,
            deviceId,
            operatorId: msg.operatorId,
            operatorName: msg.operatorName,
            sessionId: msg.sessionId,
            action: msg.action,
            timestamp: msg.timestamp,
            details: msg.details,
            payload: { ...prevLatest?.payload, ...msg.payload },
            totalMaterialDumped:
              msg.totalMaterialDumped ?? prevLatest?.totalMaterialDumped,
            currentDumped: msg.currentDumped ?? prevLatest?.currentDumped,
            syncTimestamp: msg.syncTimestamp,
          };
          return {
            ...prev,
            [deviceId]: {
              deviceId,
              latest,
              data: prev[deviceId]?.data,
            },
          };
        });
      } catch (_) {}
    };
    es.onerror = (err) => {
      console.error('SSE error:', err);
    };
    return () => {
      es.close();
    };
  }, [accessToken, customerId, customerCode]);

  const storeAssets = useAppSelector(selectAssociatedAssets);
  const assetsLoaded = useAppSelector(selectAssetsLoaded);
  useEffect(() => {
    void locationImporterApi.getLocations().then((locations) => {
      setImportedSites(
        locations
          .filter(
            (l) =>
              l.active &&
              l.centroidLat != null &&
              l.centroidLon != null &&
              l.centroidLat !== 0 &&
              l.centroidLon !== 0 &&
              Number.isFinite(l.centroidLat) &&
              Number.isFinite(l.centroidLon)
          )
          .map((l) => ({
            id: l.locationId,
            name: l.name,
            type: l.locationType as SiteZone['type'],
            latitude: l.centroidLat as number,
            longitude: l.centroidLon as number,
            altitude: 0,
            radius: 30,
            isActive: l.active,
            durationMins: l.minutes ?? undefined,
          }))
      );
    });
  }, []);
  const latestTagData = useAppSelector(selectLatestTagData);
  const latestOpData = useAppSelector(
    (state: any) =>
      state.deviceReducer.latestOperationalData as Record<string, any>
  );

  const dataReady = sensorDataReady && assetsLoaded && dispatchDataReady;

  const totalHaulTrucks = useMemo(
    () => storeAssets.filter((a) => a.assetType === 'haul_truck').length,
    [storeAssets]
  );

  const trucks = useMemo<Truck[]>(() => {
    return storeAssets
      .filter((a) => {
        const deviceId = a.device?.deviceId;
        if (!deviceId) return false;
        const hasDispatch =
          Boolean(dispatchDevices[deviceId]) ||
          Boolean(statusOverrides[deviceId]) ||
          Boolean(lastStatusMap[deviceId]);
        if (hasDispatch) return true;
        // Include GPS-only assets (e.g. excavators) so they appear on the map
        const s = latestTagData[deviceId];
        if (!s) return false;
        const latRaw = s.gps_latitude ?? s.latitude ?? s.lat;
        const lonRaw = s.gps_longitude ?? s.longitude ?? s.lon;
        const lat = typeof latRaw === 'number' ? latRaw : parseFloat(latRaw);
        const lon = typeof lonRaw === 'number' ? lonRaw : parseFloat(lonRaw);
        return Number.isFinite(lat) && Number.isFinite(lon);
      })
      .map((a) => {
        const deviceId = a.device?.deviceId ?? '';
        const d = dispatchDevices[deviceId];
        const p = d?.latest?.payload;
        const tagData = latestTagData[deviceId];
        const rawFuel = tagData?.cdl_engine_fuel_level_percent;

        const sseStatus = statusOverrides[deviceId] ?? '';
        const lastPubStatus = lastStatusMap[deviceId]?.status ?? '';
        const truckStatusRaw =
          sseStatus || lastPubStatus || (p?.truckStatus ?? '');
        const truckStatusLower = truckStatusRaw.toLowerCase();
        const status: Truck['status'] =
          truckStatusLower === 'down' || truckStatusLower === 'maintenance'
            ? 'down'
            : truckStatusLower === 'idle' ||
              truckStatusLower === 'fueling' ||
              truckStatusLower === 'queing' ||
              truckStatusLower === 'queuing'
            ? 'idle'
            : 'active';

        const activityMap: Record<string, string> = {
          hauling: t('hauling'),
          loading: t('loading'),
          returning: t('returning'),
          queued: t('queued'),
          idle: t('idle'),
          down: t('down'),
          fueling: t('fueling'),
          queing: t('queuing'),
          queuing: t('queuing'),
          maintenance: t('maintenance'),
        };

        return {
          id: deviceId,
          bumperNumber: a.bumperNumber,
          operator: d?.latest?.operatorName ?? '',
          assetType: a.assetType as AssetType,
          make: a.make,
          model: a.model,
          status,
          fuelLevel: typeof rawFuel === 'number' ? rawFuel : null,
          efficiency:
            p?.efficiency != null
              ? Math.round(p.efficiency)
              : Math.round(latestOpData[deviceId]?.tonsPerHour ?? 0),
          trips: {
            current: p?.completedTrips ?? 0,
            target: p?.targetTrips ?? 0,
          },
          payload: {
            current: p?.totalPayload ?? 0,
            capacity: p?.targetProduction ?? 0,
          },
          lastDumped: p?.materialDumped ?? d?.latest?.currentDumped ?? 0,
          currentActivity: activityMap[truckStatusLower] ?? '',
          truckStatus: truckStatusRaw,
          lastStatus: lastStatusMap[deviceId] ?? null,
          assignedLoadingPoint: p?.routeFrom ?? null,
          assignedDumpPoint: p?.routeTo ?? null,
          lastSync: d?.latest?.syncTimestamp ?? null,
          shiftEnded: Boolean(shiftEndedDevices[deviceId]),
        } satisfies Truck;
      });
  }, [
    storeAssets,
    dispatchDevices,
    latestTagData,
    latestOpData,
    statusOverrides,
    lastStatusMap,
    shiftEndedDevices,
  ]);

  // Ignition status per device from sensor API
  const ignitionMap = useMemo(() => {
    const map: Record<string, boolean> = {};
    storeAssets.forEach((a) => {
      const deviceId = a.device?.deviceId;
      if (!deviceId) return;
      const s = latestTagData[deviceId];
      map[deviceId] =
        s?.sb_ignition_status?.toString().toLowerCase() !== 'false' &&
        s?.sb_ignition_status !== undefined;
    });
    return map;
  }, [storeAssets, latestTagData]);

  // Live GPS from sensor API — keyed by real device IDs
  const livePositions = useMemo(() => {
    const map: Record<string, { lat: number; lon: number; alt: number }> = {};
    const parseGps = (
      s: Record<string, string | number>
    ): { lat: number; lon: number; alt: number } | null => {
      // Try common GPS field name variants; values may arrive as strings or numbers
      const latRaw = s.gps_latitude ?? s.latitude ?? s.lat;
      const lonRaw = s.gps_longitude ?? s.longitude ?? s.lon;
      const altRaw = s.gps_altitude ?? s.altitude ?? s.alt ?? 0;
      const lat = typeof latRaw === 'number' ? latRaw : parseFloat(latRaw);
      const lon = typeof lonRaw === 'number' ? lonRaw : parseFloat(lonRaw);
      if (!Number.isFinite(lat) || !Number.isFinite(lon)) return null;
      const altParsed =
        typeof altRaw === 'number' ? altRaw : parseFloat(altRaw);
      const alt = Number.isFinite(altParsed) ? altParsed : 0;
      return { lat, lon, alt };
    };
    storeAssets.forEach((a) => {
      const deviceId = a.device?.deviceId;
      if (!deviceId) return;
      const s = latestTagData[deviceId];
      if (!s) return;
      const pos = parseGps(s);
      if (pos) map[deviceId] = pos;
    });
    return map;
  }, [storeAssets, latestTagData]);

  const aggregatedKpiData = useMemo(() => {
    const devices = Object.values(dispatchDevices);
    const productionActual = devices.reduce(
      (s, d) => s + (d.latest?.payload?.totalPayload ?? 0),
      0
    );
    const productionTarget = devices.reduce(
      (s, d) => s + (d.latest?.payload?.targetProduction ?? 0),
      0
    );
    const tripsActual = devices.reduce(
      (s, d) => s + (d.latest?.payload?.completedTrips ?? 0),
      0
    );
    const tripsTarget = devices.reduce(
      (s, d) => s + (d.latest?.payload?.targetTrips ?? 0),
      0
    );
    const effValues = trucks.map((t) => t.efficiency).filter((v) => v > 0);
    const avgEfficiency =
      effValues.length > 0
        ? Math.round(effValues.reduce((s, v) => s + v, 0) / effValues.length)
        : 0;
    return {
      productionActual,
      productionTarget,
      tripsActual,
      tripsTarget,
      avgEfficiency,
    };
  }, [dispatchDevices, trucks, latestOpData]);

  const truckNamesMap = useMemo(() => {
    const map: Record<string, string> = {};
    storeAssets.forEach((a) => {
      const deviceId = a.device?.deviceId;
      if (deviceId && a.bumperNumber) map[deviceId] = a.bumperNumber;
    });
    return map;
  }, [storeAssets]);

  // Only haul trucks appear in the left-side list and panels; all others (excavators etc.) are map-only
  const trucksForList = useMemo(
    () => trucks.filter((t) => t.assetType === 'haul_truck'),
    [trucks]
  );

  const kpis: KpiData = useMemo(
    () => ({
      production: {
        actual: aggregatedKpiData.productionActual,
        target: aggregatedKpiData.productionTarget,
        unit: 't',
      },
      efficiency: {
        value: aggregatedKpiData.avgEfficiency,
        unit: 'T/hr',
        trend: '',
      },
      trips: {
        done: aggregatedKpiData.tripsActual,
        target: aggregatedKpiData.tripsTarget,
      },
      trucksActive: { active: 0, total: 0 },
      activeAlerts: { count: 0, critical: 0, warning: 0 },
      fuelUsed: { value: 0, unit: 'L' },
      utilization: { value: 0, target: 0, unit: '%' },
      avgCycleTime: { value: 0, target: 0, unit: 'min' },
      costPerTon: { value: 0, unit: '$' },
      payloadCompliance: { value: 0, unit: '%' },
      activeDelays: { active: 0, total: 0, totalMinutes: 0 },
    }),
    [aggregatedKpiData]
  );

  const sites = importedSites;

  if (!dataReady) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: 'calc(100vh - 108px)',
          background: bg,
          gap: '16px',
        }}
      >
        <Box
          sx={{
            width: '36px',
            height: '36px',
            border: `3px solid ${borderColor}`,
            borderTopColor: '#6366f1',
            borderRadius: '50%',
            animation: 'spin 0.8s linear infinite',
            '@keyframes spin': { to: { transform: 'rotate(360deg)' } },
          }}
        />
        <Typography sx={{ fontSize: '12px', color: textMuted }}>
          {t('dm_loading_dispatch_data')}
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: 'calc(100vh - 108px)',
        background: bg,
        color: textPrimary,
        overflow: 'hidden',
        fontFamily: `-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif`,
      }}
    >
      {/* Top bar */}
      <Box
        sx={{
          background: topBarBg,
          borderBottom: `1px solid ${borderColor}`,
          padding: '0 16px',
          height: '44px',
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          flexShrink: 0,
          zIndex: 10,
        }}
      >
        <Box
          sx={{
            ml: 'auto',
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
          }}
        >
          <Typography
            sx={{
              fontSize: '11px',
              color: textMuted,
              fontVariantNumeric: 'tabular-nums',
            }}
          >
            {currentTime}
          </Typography>
        </Box>
      </Box>

      {/* KPI bar */}
      <KpiBar
        kpis={kpis}
        enableDarkTheme={enableDarkTheme}
        activeTrucks={trucksForList.filter((t) => t.status === 'active').length}
        totalHaulTrucks={totalHaulTrucks}
      />
      {/* Main content */}
      <Box
        sx={{
          display: 'flex',
          flex: 1,
          gap: '12px',
          padding: '12px',
          overflow: 'hidden',
          minHeight: 0,
        }}
      >
        {/* Left panel with Trucks / Assignments tabs */}
        <Box
          sx={{
            width: '340px',
            minWidth: '300px',
            flexShrink: 0,
            display: 'flex',
            flexDirection: 'column',
            border: `1px solid ${borderColor}`,
            borderRadius: '10px',
            overflow: 'hidden',
            background: enableDarkTheme
              ? palette.boxBackground
              : palette.boxBackground,
          }}
        >
          {/* Tab bar */}
          <Box
            sx={{
              display: 'flex',
              borderBottom: `1px solid ${borderColor}`,
              background: enableDarkTheme
                ? palette.toolBarBackground
                : palette.toolBarBackground,
              flexShrink: 0,
              px: '4px',
            }}
          >
            {(['trucks', 'logs'] as const).map((tab) => {
              const label =
                tab === 'trucks' ? t('dm_tab_trucks') : t('dm_tab_logs');
              const isActive = activeTab === tab;
              return (
                <Box
                  key={tab}
                  onClick={() => {
                    setActiveTab(tab as any);
                  }}
                  sx={{
                    padding: '10px 14px',
                    fontSize: '11px',
                    fontWeight: isActive ? 700 : 500,
                    color: isActive ? 'primary.main' : textMuted,
                    borderBottom: isActive
                      ? '2px solid'
                      : '2px solid transparent',
                    borderBottomColor: isActive
                      ? 'primary.main'
                      : 'transparent',
                    cursor: 'pointer',
                    userSelect: 'none',
                    transition: 'all 0.15s',
                    '&:hover': { color: textPrimary },
                  }}
                >
                  {label}
                </Box>
              );
            })}
          </Box>

          {/* Tab content */}
          <Box
            sx={{
              flex: 1,
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            {activeTab === 'trucks' && (
              <TruckList
                trucks={trucksForList}
                totalHaulTrucks={totalHaulTrucks}
                selectedId={selectedTruckId}
                onSelect={setSelectedTruckId}
                onOverride={setOverrideTruckId}
                enableDarkTheme={enableDarkTheme}
                noBorder
              />
            )}
            {activeTab === 'assignments' && (
              <AssignmentPanel
                trucks={trucksForList}
                sites={sites}
                selectedId={selectedTruckId}
                onSelectTruck={(id) => {
                  setSelectedTruckId((prev) => (prev === id ? null : id));
                }}
                enableDarkTheme={enableDarkTheme}
              />
            )}
            {activeTab === 'logs' && (
              <LogsPanel
                devices={dispatchDevices}
                truckNames={truckNamesMap}
                liveLogs={liveLogs}
                onToggle={setLiveLogs}
                enableDarkTheme={enableDarkTheme}
                timezone={timezoneBasedOnDisplayMode ?? 'UTC'}
              />
            )}
          </Box>
        </Box>

        {/* Map panel — full height, no tabs */}
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            position: 'relative',
            border: `1px solid ${borderColor}`,
            borderRadius: '10px',
            overflow: 'hidden',
          }}
        >
          {/* Tracking badge overlay */}
          {selectedTruckId && (
            <Box
              sx={{
                position: 'absolute',
                top: 10,
                left: 12,
                zIndex: 20,
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                background: enableDarkTheme
                  ? 'rgba(10,15,30,0.82)'
                  : 'rgba(255,255,255,0.9)',
                border: `1px solid ${borderColor}`,
                borderRadius: '6px',
                padding: '4px 10px',
                backdropFilter: 'blur(8px)',
              }}
            >
              <Box
                sx={{
                  width: '7px',
                  height: '7px',
                  borderRadius: '50%',
                  background: '#6366f1',
                  boxShadow: '0 0 5px #6366f1',
                  flexShrink: 0,
                }}
              />
              <Typography sx={{ fontSize: '10px', color: '#a5b4fc' }}>
                {t('dm_tracking')}{' '}
                {trucks.find((t) => t.id === selectedTruckId)?.bumperNumber ??
                  selectedTruckId}
              </Typography>
              <Box
                onClick={() => {
                  setSelectedTruckId(null);
                }}
                sx={{
                  fontSize: '10px',
                  color: textMuted,
                  cursor: 'pointer',
                  padding: '1px 6px',
                  borderRadius: '3px',
                  border: `1px solid ${borderColor}`,
                  '&:hover': { color: textPrimary },
                }}
              >
                {t('dm_clear')}
              </Box>
            </Box>
          )}

          <DispatchMap
            trucks={trucks}
            sites={sites}
            livePositions={livePositions}
            ignitionMap={ignitionMap}
            selectedId={selectedTruckId}
            onTruckClick={(id) => {
              setSelectedTruckId((prev) => (prev === id ? null : id));
            }}
            onOverride={setOverrideTruckId}
            enableDarkTheme={true}
            trafficEvents={trafficEvents}
          />
        </Box>
      </Box>

      <OverridePlanModal
        open={overrideTruckId !== null}
        onClose={() => {
          setOverrideTruckId(null);
        }}
        initialTruckId={overrideTruckId ?? undefined}
      />
    </Box>
  );
};

export default DispatchMonitorView;
